import * as LodashFP from 'lodash/fp'

import {
  appError,
  HttpCommand,
  resultError,
  resultSuccess,
} from '../../interfaces/base'
import { isSavedProject, ProjectId } from '../../interfaces/entities/project'
import { GetCampaigns } from '../../interfaces/services/campaigns'
import { GetClient, GetClients } from '../../interfaces/services/clients'
import {
  AssignProjectAccessToUser,
  CopyProject,
  CopyProjectPermissionsFromUser,
  CreateFormatset,
  DoProjectAction,
  GetFormatsets,
  GetPlatforms,
  GetProject,
  GetProjectForPreview,
  GetProjects,
  GetProjectStatuses,
  GetProjectsUserHasAccessTo,
  ImportFormatsets,
  RemoveFormatSet,
  SaveProject,
  SearchProjects,
  SendEmail,
  IProjectService,
  SetProjetcsComment,
  ToggleFavoriteProject,
  UpdateFormatset,
  UpdateFormatsetPosition,
} from '../../interfaces/services/projects'
import { decode, doHttp, encode } from '../../utils/json-api'

import { ClientId } from '../../interfaces/entities/client'
import { IEmail } from '../../interfaces/entities/email'
import { FormatsetId } from '../../interfaces/entities/formatset'
import { appErrorFromAnything } from '../../utils/error'
import { translateWithUserLocale } from '../../utils/i18n'
import adapters from './adapters'

const projectService: (DI: any) => IProjectService = (DI) => {
  const getProjects: GetProjects = async (params = {}) => {
    try {
      const response = await doHttp({
        method: 'get',
        url: `/api/me/projects`,
        config: {
          params: {
            per_page: params.perPage || 25,
            includes: params.includes || undefined,
          },
        },
      })

      const adapted = decode(adapters.projects, response)
      return resultSuccess(adapted)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const getProjectForPreview: GetProjectForPreview = async (id) => {
    try {
      const response = await doHttp({
        method: 'get',
        url: `/api/projects/generate_preview_link/${id}`,
      })
      return resultSuccess(response.meta.preview_links)
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  const copyProject: CopyProject = async (id) => {
    try {
      const response = await doHttp({
        method: 'post',
        url: `/api/projects/${id}/copy`,
        data: {},
      })

      return resultSuccess({
        data: response.data,
        invalidFormatsets:
          response.meta.invalid_formatsets.map((e: number) => e.toString()) ||
          [],
      })
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  const getPlatforms: GetPlatforms = async (params = {}) => {
    try {
      const response = await doHttp({
        method: 'get',
        url: `/api/me/platforms`,
      })
      const adapted = decode(adapters.platforms, response)
      return resultSuccess(adapted)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const searchProjects: SearchProjects = async (filter, pagination) => {
    try {
      const params = {
        name: filter.query || undefined,
        client_ids:
          filter.clients.length > 0
            ? filter.clients.map((client) => client.id).join(',')
            : undefined,
        states:
          filter.statuses.length > 0
            ? filter.statuses.map((status) => status.name).join(',')
            : undefined,
        page: filter.pageNo ? filter.pageNo : pagination.pageNo,
        per_page: pagination.resultsPerPage,
        order_by: pagination.ordering.columnName.length
          ? pagination.ordering.columnName
          : undefined,
        order: pagination.ordering.columnName.length
          ? pagination.ordering.direction
          : undefined,
        favorite: !!filter.favorite,
      }
      const response = await doHttp({
        method: 'get',
        url: `/api/me/projects`,
        data: { params },
      })

      const decoded = decode(adapters.projects, response)
      const decodedPagination = decode(adapters.pagination, response)

      return resultSuccess({
        projects: decoded,
        pagination: {
          page: decodedPagination.page,
          totalRows: decodedPagination.totalRows,
          next: decodedPagination.next,
          previous: decodedPagination.previous,
        },
      })
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const getProject: GetProject = async (id) => {
    try {
      // TODO: Handle error
      const response = await doHttp({
        method: 'get',
        url: `/api/projects/${id}?includes=formatsets.creatives,comment&formatset_blueprint=true`,
      })
      const decoded = decode(adapters.project, response, 'withNextAction', {
        id,
      })

      // removed overriding blueprint for creatives in #50662 task
      return resultSuccess(decoded)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const saveProject: SaveProject = async (project) => {
    try {
      const target: HttpCommand = isSavedProject(project)
        ? {
            method: 'put',
            url: `/api/me/projects/${project.id}`,
            data: encode(adapters.project, project),
          }
        : {
            method: 'post',
            url: '/api/me/projects',
            data: encode(adapters.unsavedProject, project),
          }

      const response = await doHttp(target)
      const decoded = decode(adapters.project, response)

      return resultSuccess({
        project: decoded,
        created: !isSavedProject(project),
      })
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const getProjectsUserHasAccessTo: GetProjectsUserHasAccessTo = async (
    userId,
    clientId,
  ) => {
    try {
      const response = await doHttp({
        method: 'get',
        url: `api/project_accesses?user_id=${userId}&client_id=${clientId}&includes=project`,
      })

      const decoded = decode(adapters.projectAccess, response)

      return resultSuccess(decoded)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const getClient: GetClient = async (id, advertiserContacts, projectId) => {
    try {
      const response = await doHttp({
        method: 'get',
        url: `/api/me/memoria_clients/${id}?includes=pm_contacts,client_contacts,advertiser_users&advertiser_contacts=${advertiserContacts}&project_id=${projectId}`,
      })

      const adapted = decode(adapters.client, response, 'default', { id })

      return resultSuccess(adapted)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const getClients: GetClients = async () => {
    try {
      const response = await doHttp({
        method: 'get',
        url: `/api/me/memoria_clients?includes=pm_contacts,client_contacts,advertiser_users`,
      })
      const adapted = decode(adapters.clients, response)

      return resultSuccess(adapted)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const getProjectStatuses: GetProjectStatuses = async () => {
    try {
      const response = await doHttp({
        method: 'get',
        url: `/api/me/projects?per_page=1`,
      })

      const possibleStates = response.meta.possible_states
      const statuses = Object.keys(possibleStates).map((statusName) => {
        const statusTitle = possibleStates[statusName]

        return {
          name: statusName,
          title: statusTitle,
          description: statusTitle,
        }
      })

      return resultSuccess(statuses)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const doProjectAction: DoProjectAction = async (action, project, options) => {
    try {
      const target: HttpCommand = {
        method: 'post',
        url: `/api/me/projects/change_state`,
        data: {
          ids: [project.id],
          transition: action.name,
          reopen_creatives: LodashFP.getOr(
            undefined,
            'reopenCreatives',
            options,
          ),
        },
      }
      const response = await doHttp(target)
      if (response.status) {
        return resultError(
          appError({
            title: translateWithUserLocale('whoops') + '!',
            message: LodashFP.flattenDeep(response.data.errors).join(','),
          }),
        )
      }
      const adapted = decode(adapters.project, response, 'default', {
        id: project.id,
      })

      return resultSuccess(adapted)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const getCampaigns: GetCampaigns = async (client) => {
    try {
      const response = await doHttp({
        method: 'get',
        url: `/api/me/memoria_clients/${client.id}?includes=memoria_campaigns`,
      })
      const adapted = decode(adapters.campaigns, response)

      return resultSuccess(adapted)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const getFormatsets: GetFormatsets = async (filter = {}) => {
    try {
      const params = {
        project_id: filter.projectId || undefined,
        includes: filter.includes || undefined,
      }
      const response = await doHttp({
        method: 'get',
        url: `/api/formatsets`,
        data: { params },
      })
      const adapted = decode(adapters.formatsets, response)
      return resultSuccess(adapted)
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const createFormatset: CreateFormatset = async (params) => {
    try {
      const response = await doHttp({
        method: 'post',
        url: 'api/formatsets',
        data: {
          name: params.name,
          project_id: params.projectId,
          type: params.type,
          formatset: params.data,
        },
      })

      const adapted = decode(adapters.formatset, response)

      return resultSuccess(adapted)
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.create_formatset'),
      })
    }
  }

  const importFormatsets: ImportFormatsets = async (
    projectId: ProjectId,
    ids: FormatsetId[],
    platform: string,
  ) => {
    try {
      const response = await doHttp({
        method: 'post',
        url: `api/formatsets/import`,
        data: {
          platform: LodashFP.camelCase(platform),
          project_id: projectId,
          ids: ids,
        },
      })

      return resultSuccess({
        invalidFormatsets: response.meta
          .find((res: any) => res.key === 'invalid_formatsets')
          .value.map((val: number) => val.toString()),
        result: response.meta.find((res: any) => res.key === 'result').value,
      })
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.import_formatset'),
      })
    }
  }

  const updateFormatset: UpdateFormatset = async (params) => {
    try {
      const response = await doHttp({
        method: 'put',
        url: `api/formatsets/${params.formatsetId}`,
        data: {
          name: params.name,
          project_id: params.projectId,
          type: params.type,
          formatset: params.data,
        },
      })

      const adapted = decode(adapters.formatset, response)

      return resultSuccess(adapted)
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.create_formatset'),
      })
    }
  }

  const removeFormatset: RemoveFormatSet = async (formatsetId) => {
    try {
      const response = await doHttp({
        method: 'delete',
        url: `api/formatsets/${formatsetId}`,
      })

      return resultSuccess(response)
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.remove_formatset'),
      })
    }
  }

  const updateFormatsetPosition: UpdateFormatsetPosition = async (
    formatsetId: FormatsetId,
    displayOrder: number,
  ) => {
    try {
      const target: HttpCommand = {
        method: 'post',
        url: `/api/formatsets/${formatsetId}/change_position`,
        data: {
          desired_position: displayOrder,
        },
      }

      const response = await doHttp(target, { expectedErrorCodes: [403, 404] })
      if (response.status === 403 || response.status === 404) {
        return resultError({
          statusCode: `${response.status}`,
          title: translateWithUserLocale('whoops') + '!',
          message: translateWithUserLocale('error.something_went_wrong'),
        })
      }

      const decoded = decode(adapters.formatset, response)
      return resultSuccess(decoded)
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.something_went_wrong'),
      })
    }
  }

  const setProjectComment: SetProjetcsComment = async (
    projectId: ProjectId,
    comment: string,
  ) => {
    try {
      const response = await doHttp({
        method: 'patch',
        url: `api/projects/${projectId}/relationships/comments`,
        data: {
          body: comment,
        },
      })

      const adapted = decode(adapters.comment, response)

      return resultSuccess(adapted)
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.save_comment'),
      })
    }
  }

  const sendEmail: SendEmail = async (email: IEmail, clientId: ClientId) => {
    try {
      const response = await doHttp(
        {
          method: 'post',
          url: `api/me/projects/send_mail/${clientId}`,
          data: email,
        },
        { throwOnError: true, expectedErrorCodes: [422] },
      )

      if (response.status === 422) {
        return resultError({
          statusCode: response.status,
          title: translateWithUserLocale('whoops'),
          message: translateWithUserLocale('error.send_mail'),
        })
      }

      return resultSuccess(response)
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops'),
        message: translateWithUserLocale('error.send_mail'),
      })
    }
  }

  const copyProjectPermissionsFromUser: CopyProjectPermissionsFromUser = async (
    from,
    to,
    clientId,
  ) => {
    try {
      const projectsResponse = await getProjectsUserHasAccessTo(
        from.id,
        clientId,
      )
      const projects =
        projectsResponse.type === 'success' ? projectsResponse.payload : []

      await doHttp({
        method: 'post',
        url: `api/project_accesses/${from.id}/copy_permissions`,
        data: {
          user_id: to.id,
          client_id: clientId,
        },
      })

      return resultSuccess({
        user: {
          ...to,
          restrictedAccess: from.restrictedAccess,
        },
        projects,
      })
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops'),
        message: translateWithUserLocale('error.send_mail'),
      })
    }
  }

  const assignProjectAccess: AssignProjectAccessToUser = async (
    user,
    projects,
    clientId,
  ) => {
    try {
      await doHttp({
        method: 'put',
        url: `api/project_accesses/${user.id}`,
        data: {
          project_ids: projects.map((p) => p.id),
          client_id: clientId,
        },
      })

      return resultSuccess({
        user,
        projects,
      })
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops'),
        message: translateWithUserLocale('error.send_mail'),
      })
    }
  }

  const toggleFavoriteProject: ToggleFavoriteProject = async (
    id,
    isFavorite,
  ) => {
    const method = isFavorite ? 'delete' : 'post'

    try {
      await doHttp({
        method,
        url: `api/me/projects/${id}/favorite`,
        data: {},
      })
      return resultSuccess('success')
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  return {
    searchProjects,
    getProjects,
    getProject,
    getProjectStatuses,
    saveProject,
    doProjectAction,
    setProjectComment,

    getFormatsets,
    createFormatset,
    updateFormatset,
    removeFormatset,
    updateFormatsetPosition,
    importFormatsets,
    getCampaigns,
    getClients,
    getClient,

    sendEmail,

    getProjectsUserHasAccessTo,
    copyProjectPermissionsFromUser,
    assignProjectAccess,
    getProjectForPreview,
    copyProject,

    getPlatforms,

    toggleFavoriteProject,
  }
}

export default projectService
